import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from '@els/els-react--table';
import InnerHTML from '@Components/InnerHTML';
import ShowMore from '@Components/Results/utils/ShowMore';
import {
  AGGREGATOR_OFFERS_TABLE_CAPTION,
  AGGREGATOR_PREVIEW_LIMIT,
  COMMERCIAL_DETAILS_SHOW_MORE_THRESHOLD,
  DEFAULT_DETAIL_SORT_STATE,
} from './constants';
import {
  buildAggregatorPreviewRows,
  formatPriceValueHtml,
  getExpandedDetailColumns,
  hasSinglePriceCurrency,
} from './SubstanceCommercialSupplierInfo.helpers';
import {
  buildAggregatorDetailTableColumns,
  EMPTY_OFFERS,
  getOfferStackItemKey,
  getVisibleOfferStackItems,
  prepareDetailRowForLeydenTable,
  useDetailSortState,
} from './SubstanceCommercialSupplierDetailTable.helpers';
import SubstanceCommercialSupplierDetailTableHead from './SubstanceCommercialSupplierDetailTableHead';

import './SubstanceCommercialSupplierDetailTables.scss';

function SubstanceCommercialSupplierAggregatorPreviewTable({ group }) {
  const { sortState, handleSort } = useDetailSortState();
  const [visibleSupplierCount, setVisibleSupplierCount] = useState(AGGREGATOR_PREVIEW_LIMIT);
  const [sortAnnouncement, setSortAnnouncement] = useState('');
  const aggregatorRows = group.aggregatorRows || EMPTY_OFFERS;
  const columns = useMemo(() => getExpandedDetailColumns(aggregatorRows), [aggregatorRows]);
  const isPriceSortable = columns.showPrice && hasSinglePriceCurrency(aggregatorRows);
  const effectiveSortState = sortState.field === 'price' && !isPriceSortable ? DEFAULT_DETAIL_SORT_STATE : sortState;
  const tableColumns = useMemo(() => buildAggregatorDetailTableColumns(columns), [columns]);
  const previewRows = useMemo(
    () => buildAggregatorPreviewRows(aggregatorRows, visibleSupplierCount, effectiveSortState),
    [aggregatorRows, visibleSupplierCount, effectiveSortState]
  );
  const tableRows = useMemo(() => previewRows.map(prepareDetailRowForLeydenTable), [previewRows]);

  return (
    <div className="rcs-commercial-aggregator-preview" data-e2e={`aggregator-preview-${group.supplierId}`}>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {sortAnnouncement}
      </span>
      <Table
        a11y={{
          name: 'Aggregator product offers',
          description: AGGREGATOR_OFFERS_TABLE_CAPTION,
        }}
        className="rx-table rx-table--full-width rcs-commercial-details-table rcs-commercial-details-table--aggregator"
        columns={tableColumns}
        data={tableRows}
        id={`aggregator-preview-${group.supplierId}-offers`}
        rowKey="key"
        subtleDividers
        useSemantic
        verticalDividers
        renderHead={() => (
          <SubstanceCommercialSupplierDetailTableHead
            tableColumns={tableColumns}
            handleSort={handleSort}
            isPriceSortable={isPriceSortable}
            onSortAnnouncement={setSortAnnouncement}
            priceSortTooltipId={`aggregator-preview-${group.supplierId}-price-sort-tooltip`}
            sortState={sortState}
          />
        )}
        renderDatum={(rows = []) =>
          rows.map((previewRow) => {
            const stackOffers = getVisibleOfferStackItems(previewRow?.offers);

            return (
              <tr
                key={previewRow.key}
                className={`c-els-table__row no-hover-background ${previewRow.isSubgroupEnd ? '' : 'rcs-commercial-details-table__row--continued'}`}>
                {!previewRow.supplierNameHidden && (
                  <td rowSpan={previewRow.supplierNameRowSpan} className="column-supplier c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.supplierName || ''}`} />
                  </td>
                )}
                {columns.showPurity && !previewRow.purityValueHidden && (
                  <td rowSpan={previewRow.purityValueRowSpan} className="column-purity c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.purityValue || ''}`} />
                  </td>
                )}
                {columns.showPackageSize && (
                  <td className="c-els-table__cell c-els-table__cell--stacked">
                    <div className="rcs-commercial-details-table__stack">
                      {stackOffers.map((offer, offerIndex) => (
                        <InnerHTML
                          tag="div"
                          key={getOfferStackItemKey(previewRow.key, offerIndex, 'package')}
                          className="rcs-commercial-details-table__stack-item"
                          html={`${offer.packageSize || ''}`}
                        />
                      ))}
                    </div>
                  </td>
                )}
                {columns.showPrice && (
                  <td className="c-els-table__cell c-els-table__cell--stacked">
                    <div className="rcs-commercial-details-table__stack">
                      {stackOffers.map((offer, offerIndex) => (
                        <InnerHTML
                          tag="div"
                          key={getOfferStackItemKey(previewRow.key, offerIndex, 'price')}
                          className="rcs-commercial-details-table__stack-item"
                          html={formatPriceValueHtml(offer)}
                        />
                      ))}
                    </div>
                  </td>
                )}
                {columns.showShipmentCountry && (
                  <td className="c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.shipmentCountryValue || ''}`} />
                  </td>
                )}
                {columns.showShipping && !previewRow.shippingValueHidden && (
                  <td className="c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.shippingValue || ''}`} />
                  </td>
                )}
                {columns.showAvailability && <td className="c-els-table__cell">{previewRow.availabilityValue || ''}</td>}
              </tr>
            );
          })
        }
      />

      {group.aggregatorSupplierCount > AGGREGATOR_PREVIEW_LIMIT && (
        <div className="rcs-commercial-aggregator-preview__footer">
          <ShowMore
            hideIcon
            preventFocusScroll
            idSuffix={`aggregator-${group.supplierId}`}
            container=".rcs-commercial-detail-panel"
            threshold={COMMERCIAL_DETAILS_SHOW_MORE_THRESHOLD}
            initial={AGGREGATOR_PREVIEW_LIMIT}
            current={Math.min(visibleSupplierCount, group.aggregatorSupplierCount)}
            total={group.aggregatorSupplierCount}
            moreHandler={({ lastItem }) => setVisibleSupplierCount(lastItem)}
            lessHandler={() => setVisibleSupplierCount(AGGREGATOR_PREVIEW_LIMIT)}
          />
        </div>
      )}
    </div>
  );
}

SubstanceCommercialSupplierAggregatorPreviewTable.propTypes = {
  group: PropTypes.object.isRequired,
};

export default SubstanceCommercialSupplierAggregatorPreviewTable;
